import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "Components/DesignSystem/Toast/useToast";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import { Cross } from "Components/DesignSystem/Iconography/Icons";
import ModalContainer from "Components/Common/Modal/ModalContainer";

type TagCellProps = {
    value: string;
    onChange: (tag: string) => void;
    allTags: string[];
    disabled?: boolean;
    'data-testid'?: string;
};

const MAX_TAG_LENGTH = 18;

const TagCell = ({ value, onChange, allTags, disabled = false, 'data-testid': dataTestId = 'list-tagcell' }: TagCellProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(value || "");
    const [showDropdown, setShowDropdown] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isEditing) setInputValue(value || "");
    }, [value, isEditing]);

    const filteredTags = allTags.filter(
        (tag) => tag.toLowerCase().includes(inputValue.toLowerCase())
    );

    const refreshDropdownPos = () => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    };

    const handleActivate = () => {
        if (disabled) return;
        setInputValue(value || "");
        setIsEditing(true);
        setShowDropdown(true);
        setTimeout(() => {
            inputRef.current?.focus();
            refreshDropdownPos();
        }, 0);
    };

    const handleCommit = () => {
        const trimmed = inputValue.trim();
        onChange(trimmed);
        setIsEditing(false);
        setShowDropdown(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val.length > MAX_TAG_LENGTH) return;
        setInputValue(val);
        setShowDropdown(true);
        refreshDropdownPos();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleCommit();
        } else if (e.key === "Escape") {
            setInputValue(value || "");
            setIsEditing(false);
            setShowDropdown(false);
        }
    };

    const handleSelect = (tag: string) => {
        onChange(tag);
        setInputValue(tag);
        setIsEditing(false);
        setShowDropdown(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowClearModal(true);
    };

    const confirmClear = () => {
        onChange("");
        setInputValue("");
        setShowClearModal(false);
        toast.success("Tag removed successfully");
    };

    return (
        <div ref={containerRef} className="tw-w-[95%]">
            <div role="button" data-testid={`${dataTestId}-div-1`}
                className={`tw-flex tw-items-center tw-h-9 tw-px-2.5 tw-rounded tw-border tw-bg-surface ${isEditing ? "tw-border-accent" : "tw-border-edge-strong"} ${!isEditing && !disabled ? "tw-cursor-pointer" : ""}`}
                onClick={!isEditing ? handleActivate : undefined}
            >
                {isEditing ? (
                    <input data-testid={`${dataTestId}-input-1`}
                        ref={inputRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleCommit}
                        onKeyDown={handleKeyDown}
                        maxLength={MAX_TAG_LENGTH}
                        placeholder="Add Tag"
                        className="tw-flex-1 tw-min-w-0 tw-w-full tw-bg-transparent tw-outline-none tw-text-sm tw-text-ink placeholder:tw-text-ink-secondary"
                    />
                ) : (
                    <>
                        <span className={`tw-flex-1 tw-min-w-0 tw-text-sm tw-truncate ${value ? "tw-text-ink" : "tw-text-ink-tertiary"}`}>
                            {value || "Add Tag"}
                        </span>
                        {value && !disabled && (
                            /* § 212 — was a nameless text-glyph ✕ with no hit target.
                               The popup's twin (button-3) stays legacy with its
                               ModalContainer shell. */
                            <IconButton data-testid={`${dataTestId}-button-2`}
                                size="sm"
                                aria-label="Clear tag"
                                className="tw-ml-1"
                                onClick={handleClear}
                            >
                                <Cross size={12} />
                            </IconButton>
                        )}
                    </>
                )}
            </div>
            {isEditing && showDropdown && filteredTags.length > 0 && ReactDOM.createPortal(
                <div
                    className="tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-md"
                    style={{
                        position: "fixed",
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        zIndex: 9999,
                        maxHeight: 200,
                        overflowY: "auto",
                    }}
                >
                    {filteredTags.map((tag, i) => (
                        <div
                            key={i}
                            className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-surface-hover tw-text-sm tw-text-ink"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(tag);
                            }}
                        >
                            <span>{tag}</span>
                            {/*<span className="tw-text-gray-400 tw-text-xs">✕</span>*/}
                        </div>
                    ))}
                </div>,
                document.body
            )}
            <ModalContainer
                id="tag-cell-clear-modal"
                isOpen={showClearModal}
                setOpen={() => setShowClearModal(false)}
                width="580px"
            >
                <div className="tw-flex tw-justify-between tw-items-center tw-px-5 tw-py-4">
                    <span className="tw-text-base tw-font-semibold tw-text-ink tw-font-['Noto Sans']">
                        Clear Tag
                    </span>
                    <button data-testid={`${dataTestId}-button-3`}
                        onClick={() => setShowClearModal(false)}
                        className="tw-text-ink-tertiary hover:tw-text-ink tw-text-lg tw-leading-none"
                        type="button"
                    >
                        ✕
                    </button>
                </div>
                <div className="tw-border-t tw-border-edge" />
                <div className="tw-px-5 tw-py-5 tw-space-y-2">
                    <p className="tw-text-sm tw-text-ink tw-font-semibold">
                        Are you sure you want to clear the tag assignment from this list item?
                    </p>
                    <p className="tw-text-sm tw-text-ink">
                        <span className="tw-font-semibold">Tag Name - </span>
                        <span className="tw-font-semibold">{value}</span>
                    </p>
                </div>
                <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-px-5 tw-pb-5">
                    <button data-testid={`${dataTestId}-button-4`}
                        onClick={() => setShowClearModal(false)}
                        className="tw-h-8 tw-px-5 tw-bg-surface tw-rounded tw-border tw-border-accent tw-text-accent tw-text-sm tw-font-normal hover:tw-bg-accent-tint"
                        type="button"
                    >
                        Cancel
                    </button>
                    <button data-testid={`${dataTestId}-button-5`}
                        onClick={confirmClear}
                        className="tw-h-8 tw-px-5 tw-bg-accent-bold tw-rounded tw-text-ink-inverse tw-text-sm tw-font-normal"
                        type="button"
                    >
                        Clear Tag
                    </button>
                </div>
            </ModalContainer>
        </div>
    );
};

export default TagCell;
